import { useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@narmi/design_system";
import { useForm } from "react-hook-form";
import { Navigate, useLocation } from "react-router";
import { z } from "zod";
import CreditCardPromo from "@lending/components/CreditCardPromo/CreditCardPromo";
import { HeroSection } from "@lending/components/HeroSection/HeroSection";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useGetCreditCardProductsQuery } from "@lending/hooks/queries/CreditCardProduct/useGetCreditCardProductsQuery";
import { useHeroBreakpoints } from "@lending/hooks/useHeroBreakpoints";
import { useIsPlaidLayerEnabled } from "@lending/hooks/useIsPlaidLayerEnabled";
import { useFlowNavigation } from "@lending/navigation";
import { InfoAlert } from "@lending/pages/Common/components/InfoAlert";
import { GetStartedFields } from "@lending/types";
import styles from "./GetStarted.module.css";
import { useStore } from "../../../contexts/StoreContext";
import type { ReactLocalization } from "@fluent/react";

function createSchema(l10n: ReactLocalization) {
  const requiredMessage = l10n.getString("error-required", null, "Required");
  const invalidEmailMessage = l10n.getString("error-invalid-email", null, "Enter a valid email");

  return z.object({
    email: z.string().min(1, { message: requiredMessage }).email({ message: invalidEmailMessage }),
  });
}

function GetStarted() {
  const { l10n } = useLocalization();
  const schema = createSchema(l10n);
  const { goNext, goToStep } = useFlowNavigation();
  const location = useLocation();
  const { m } = useHeroBreakpoints();
  const isPlaidLayerEnabled = useIsPlaidLayerEnabled();
  const { data: storeData, updateData } = useStore();
  const { creditCardProductsData } = useGetCreditCardProductsQuery();
  const previousGetStartedState = location.state as GetStartedFields;

  const { handleSubmit, control } = useForm<GetStartedFields>({
    defaultValues: {
      email: previousGetStartedState?.email ?? storeData.email ?? "",
    },
    resolver: zodResolver(schema),
  });
  const product = creditCardProductsData.results[0];

  if (creditCardProductsData.count !== 1 || !product) {
    return <Navigate to="/credit-card/select-card" replace />;
  }

  function onSubmit(data: GetStartedFields) {
    updateData({ email: data.email });

    if (isPlaidLayerEnabled) {
      goNext();
      return;
    }

    goToStep("personal-info", {
      state: {
        from: location.pathname,
        email: data.email,
      },
    });
  }

  return (
    <>
      <PageLayout>
        <div className={styles.container}>
          <HeroSection
            title={l10n.getString(
              "welcome-screen-title-credit-cards",
              null,
              "Get a credit card that fits your needs"
            )}
            content={
              m ? (
                <div className="margin--top--xl">
                  <CreditCardPromo product={product} />
                </div>
              ) : null
            }
          />
          <div className={styles.formSectionContainer}>
            {!m && <CreditCardPromo product={product} />}
            <form onSubmit={handleSubmit(onSubmit)} className={styles.formSection} noValidate>
              <TextInput
                control={control}
                name="email"
                label={l10n.getString("personal-info-email-address-label", null, "Email address")}
                type="email"
              />
              <InfoAlert
                message={l10n.getString(
                  "welcome-screen-not-affect-credit-score",
                  null,
                  "Your application will not affect your credit score."
                )}
              />
              <div className="margin--top--l">
                <Button
                  label={l10n.getString("button-label-get-started", null, "Get started")}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export { GetStarted };
